import { useState, useCallback, useMemo, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import WhatsAppSticky from "@/components/landing/WhatsAppSticky";
import LeadDialog from "@/components/landing/LeadDialog";
import Reveal from "@/components/landing/Reveal";
import { fetchPartnerBrands, fetchSiteContent } from "@/lib/api";
import { setPageMeta } from "@/lib/pageMeta";

const resolveAsset = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const STEPS = [
  { num: "Step 01", title: "Choose Your Utsah Collection", desc: "Every hamper automatically includes access to the Exclusive Partner Benefit Program." },
  { num: "Step 02", title: "Scan the QR Code", desc: "Inside every hamper is a unique QR code. Simply scan it to access the Exclusive Partner Benefit Program and explore offers from our growing network of partner brands." },
  { num: "Step 03", title: "Browse & Redeem Exclusive Offers", desc: "Explore offers from leading brands, choose the ones that interest you and redeem them directly through the platform." },
];

const OCCASIONS = [
  "Employees", "Doctors", "Dealers", "Leadership Teams", "Distributors", "VIP Clients",
];

const VALUE_ROWS = [
  { title: "Thoughtful Gifts, Curated for Your Team", desc: "Premium gifts selected to make every gifting experience feel personal and meaningful.", badge: "Thoughtfully Curated & Gift-Ready" },
  { title: "Customised for Your Brand, Free of Charge", desc: "Add your company branding to every gift, with complimentary customisation.", badge: "Complimentary Branding Included" },
  { title: "Your Gift. Your Tree. Your Impact.", desc: "Every eligible gift helps plant a tree in your company's name, creating a lasting environmental impact.", badge: "One Tree Planted with Every Order" },
  { title: "Exclusive Offers from Brands You Love", desc: "Every hamper unlocks access to exclusive offers and discounts from our partner brands.", badge: "Access to Exclusive Partner Offers" },
];

export default function PartnerBenefitsPage() {
  const [dialog, setDialog] = useState({ open: false, type: "meeting" });
  const openMeeting = useCallback(() => setDialog({ open: true, type: "meeting" }), []);
  const openBulk = useCallback(() => setDialog({ open: true, type: "bulk" }), []);
  const closeDialog = useCallback(() => setDialog((d) => ({ ...d, open: false })), []);
  const handlers = useMemo(() => ({ openMeeting, openBulk }), [openMeeting, openBulk]);

  const [brands, setBrands] = useState([]);
  const [siteContent, setSiteContent] = useState({});
  useEffect(() => {
    let alive = true;
    fetchPartnerBrands()
      .then((list) => { if (alive && Array.isArray(list)) setBrands(list); })
      .catch(() => { /* silent — marquee simply hides when empty */ });
    fetchSiteContent().then((c) => { if (alive) setSiteContent(c || {}); }).catch(() => {});
    setPageMeta({
      title: "Partner Benefits — Utsah Corporate Gifting",
      description: "Every Utsah hamper unlocks access to the Utsah Partner Benefits Program — exclusive offers and discounts from leading brands.",
      canonicalPath: "/partner-benefits",
    });
    return () => { alive = false; };
  }, []);

  // Split published brands into two rows for the counter-scrolling marquee.
  // The list is already ordered by display_order server-side.
  const { rowA, rowB } = useMemo(() => {
    const a = [], b = [];
    brands.forEach((br, i) => (i % 2 === 0 ? a : b).push(br));
    return { rowA: a, rowB: b };
  }, [brands]);

  return (
    <div className="utsah-app">
      <Nav onBookMeeting={openMeeting} />

      {/* HERO */}
      <section className="section pbp-hero" data-testid="pbp-hero-section" style={{ paddingTop: 40 }}>
        <div className="section-inner pbp-hero-grid">
          <div>
            <Reveal className="eyebrow">Exclusive Partner Benefit Program</Reveal>
            <Reveal as="h1" delay={1} className="section-headline">
              Exclusive Benefits with <em>Every Utsah Hamper.</em>
            </Reveal>
            <Reveal as="p" delay={2} className="section-sub" style={{ marginTop: 20 }}>
              Utsah hampers include access to our Exclusive Partner Benefit Program, giving everyone who receives the hamper access to exclusive offers from leading brands through one simple digital experience.
            </Reveal>
            <Reveal delay={3}>
              <button className="hero-cta" style={{ marginTop: 32 }} onClick={openMeeting} data-testid="pbp-hero-cta">
                Book a Consultation
                <svg width="18" height="12" viewBox="0 0 18 12" fill="none" aria-hidden><path d="M1 6h15M11 1l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            </Reveal>
          </div>
          <Reveal delay={1} className="s3-image-placeholder pbp-hero-media" style={siteContent.partner_benefits_hero ? { backgroundImage: `url(${resolveAsset(siteContent.partner_benefits_hero)})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined} data-testid="pbp-hero-image">
            {!siteContent.partner_benefits_hero ? (
              <>
                <svg width="56" height="56" viewBox="0 0 64 64" fill="none" aria-hidden>
                  <rect x="8" y="16" width="48" height="36" rx="3" fill="var(--u-orange)" opacity="0.24" />
                  <rect x="14" y="10" width="36" height="8" rx="2" fill="var(--u-orange)" opacity="0.2" />
                </svg>
                <div className="add-image-label">Lifestyle / Product Feature Image</div>
              </>
            ) : null}
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section pbp-how" data-testid="pbp-how-section">
        <div className="section-inner" style={{ textAlign: "center" }}>
          <Reveal className="eyebrow" style={{ display: "inline-flex", marginLeft: "auto", marginRight: "auto" }}>How It Works</Reveal>
          <Reveal as="h2" delay={1} className="section-headline">Three Simple <em>Steps.</em></Reveal>
          <Reveal as="p" delay={2} className="section-sub" style={{ marginTop: 20, marginLeft: "auto", marginRight: "auto", maxWidth: 720 }}>
            Every Utsah hamper automatically includes access to our Exclusive Partner Benefit Program.
          </Reveal>
          <div className="pbp-steps">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i} className="pbp-step">
                <div className="pbp-step-num">{s.num}</div>
                <div className="pbp-step-title">{s.title}</div>
                <div className="pbp-step-desc">{s.desc}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER BRANDS */}
      <section className="section pbp-brands" data-testid="pbp-brands-section">
        <div className="section-inner" style={{ textAlign: "center" }}>
          <Reveal className="eyebrow" style={{ display: "inline-flex", marginLeft: "auto", marginRight: "auto" }}>Partner Brands</Reveal>
          <Reveal as="h2" delay={1} className="section-headline">
            Explore Exclusive Offers from <em>Leading Brands.</em>
          </Reveal>
          <Reveal as="p" delay={2} className="section-sub" style={{ marginTop: 20, marginLeft: "auto", marginRight: "auto", maxWidth: 820 }}>
            Through our Exclusive Partner Benefit Program, everyone who receives a Utsah hamper gains access to carefully selected offers from leading brands across fashion, dining, travel, wellness, entertainment and more—all through one simple digital experience.
          </Reveal>
        </div>
        {brands.length > 0 && (
          <div className="pbp-marquee-wrap">
            {rowA.length > 0 && (
              <div className="pbp-marquee-row pbp-marquee-left">
                <div className="pbp-marquee-track">
                  {[...rowA, ...rowA].map((b, i) => <BrandPill key={`a-${b.id}-${i}`} brand={b} />)}
                </div>
              </div>
            )}
            {rowB.length > 0 && (
              <div className="pbp-marquee-row pbp-marquee-right">
                <div className="pbp-marquee-track">
                  {[...rowB, ...rowB].map((b, i) => <BrandPill key={`b-${b.id}-${i}`} brand={b} />)}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="pbp-brands-note">Partner brands are subject to availability and may vary by season.</div>
      </section>

      {/* PERFECT FOR EVERY OCCASION */}
      <section className="section pbp-occasions" data-testid="pbp-occasions-section">
        <div className="section-inner" style={{ textAlign: "center" }}>
          <Reveal className="eyebrow" style={{ display: "inline-flex", marginLeft: "auto", marginRight: "auto" }}>Perfect For Every Occasion</Reveal>
          <Reveal as="h2" delay={1} className="section-headline">
            Supporting Every Meaningful <em>Corporate Relationship.</em>
          </Reveal>
          <Reveal as="p" delay={2} className="section-sub" style={{ marginTop: 20, marginLeft: "auto", marginRight: "auto", maxWidth: 820 }}>
            Whether you're celebrating employees, welcoming new joiners, thanking clients or recognising business partners, every Utsah hamper includes access to exclusive partner benefits.
          </Reveal>
          <div className="pbp-pills-grid">
            {OCCASIONS.map((o, i) => (
              <Reveal key={o} delay={i > 3 ? 3 : i} className="pbp-pill-card">
                <span className="pbp-pill-text">{o}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE BEYOND THE GIFT */}
      <section className="section pbp-value" data-testid="pbp-value-section">
        <div className="section-inner">
          <div style={{ textAlign: "center", maxWidth: 820, margin: "0 auto 64px" }}>
            <Reveal className="eyebrow" style={{ display: "inline-flex", marginLeft: "auto", marginRight: "auto" }}>Value Beyond the Gift</Reveal>
            <Reveal as="h2" delay={1} className="section-headline" style={{ marginTop: 16 }}>
              More Than What's <em>Inside the Hamper.</em>
            </Reveal>
            <Reveal as="p" delay={2} className="section-sub" style={{ marginTop: 24, maxWidth: 640, marginLeft: "auto", marginRight: "auto" }}>
              A thoughtful gift creates a memorable first impression. Our Exclusive Partner Benefit Program ensures the experience continues long after the hamper has been opened.
            </Reveal>
          </div>
          <div className="pbp-stack">
            {VALUE_ROWS.map((r, i) => (
              <Reveal key={r.title} delay={i > 3 ? 3 : i} className="pbp-stack-row">
                <div>
                  <div className="pbp-stack-title">{r.title}</div>
                  <div className="pbp-stack-desc">{r.desc}</div>
                </div>
                <div className="pbp-stack-badge">{r.badge}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section pbp-closing" data-testid="pbp-closing-section">
        <div className="section-inner" style={{ textAlign: "center", maxWidth: 820, margin: "0 auto" }}>
          <Reveal className="eyebrow" style={{ display: "inline-flex", marginLeft: "auto", marginRight: "auto" }}>Let's Get Started</Reveal>
          <Reveal as="h2" delay={1} className="section-headline" style={{ marginTop: 16 }}>
            Thoughtful Gifts. <em>Meaningful Moments.</em>
          </Reveal>
          <Reveal as="p" delay={2} className="section-sub" style={{ marginTop: 24, maxWidth: 640, marginLeft: "auto", marginRight: "auto" }}>
            Whether you're celebrating employees, welcoming new joiners or strengthening client relationships, we'll help you create gifting experiences that leave a lasting impression.
          </Reveal>
          <Reveal delay={3} className="pbp-cta-row" style={{ marginTop: 40 }}>
            <button className="hero-cta" onClick={openMeeting} data-testid="pbp-closing-cta">
              Book a Consultation
              <svg width="18" height="12" viewBox="0 0 18 12" fill="none" aria-hidden><path d="M1 6h15M11 1l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <a className="btn-secondary btn-secondary-on-cream" href="https://wa.me/917208286283" target="_blank" rel="noreferrer" data-testid="pbp-closing-whatsapp">Chat on WhatsApp</a>
          </Reveal>
        </div>
      </section>

      <Footer handlers={handlers} />
      <WhatsAppSticky />
      <LeadDialog open={dialog.open} type={dialog.type} source="Partner Benefits" onOpenChange={(v) => (v ? null : closeDialog())} />
      <Toaster position="bottom-center" richColors theme="light" toastOptions={{ style: { fontFamily: "Manrope, system-ui, sans-serif", fontWeight: 500 } }} />
    </div>
  );
}

function BrandPill({ brand }) {
  const src = resolveAsset(brand.logo);
  const [heightPx, setHeightPx] = useState(null);

  // Adaptive logo sizing — normalize the OPTICAL WEIGHT of each logo
  // regardless of its native aspect ratio. Wide wordmarks render shorter,
  // square / icon-style logos render taller so every brand feels balanced.
  const handleLoad = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    if (!w || !h) return;
    const ratio = w / h;
    let px;
    if (ratio >= 4.5)      px = 30;   // ultra-wide wordmark
    else if (ratio >= 3.0) px = 36;   // wide wordmark
    else if (ratio >= 2.0) px = 40;   // typical wordmark
    else if (ratio >= 1.3) px = 44;   // balanced
    else                    px = 48;   // square / icon-style
    setHeightPx(px);
  };

  // When a logo is uploaded, render it bare (no pill wrapper) so it is not
  // visually constrained by the placeholder pill's dimensions or padding.
  if (src) {
    return (
      <img
        src={src}
        alt={brand.name}
        className="pbp-brand-logo pbp-brand-logo--bare"
        loading="lazy"
        onLoad={handleLoad}
        style={heightPx ? { height: `${heightPx}px` } : undefined}
        data-testid={`pbp-brand-${brand.id}`}
      />
    );
  }

  // Fallback: no logo uploaded → keep the existing placeholder text pill.
  return (
    <span className="pbp-brand-pill" data-testid={`pbp-brand-${brand.id}`}>
      {brand.name}
    </span>
  );
}

